import { useState, type ReactNode } from "react";
import { Link, useLocation } from "wouter";
import { AppLayout } from "@/components/app-layout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useCrmText } from "@/lib/crm/i18n";
import { crmFetch, crmKeys, useCrmContext, type CrmContext } from "@/lib/crm/api";
import { queryClient } from "@/lib/queryClient";
import { Loader2, Settings } from "lucide-react";

function readUser(): any {
  try { return JSON.parse(localStorage.getItem("user") || "null"); } catch { return null; }
}

/** Turns the company CRM module on/off (company admins; the switch lives in Settings). */
export async function setCompanyCrm(enabled: boolean) {
  await crmFetch("PUT", "/api/crm/status", { enabled });
  await queryClient.invalidateQueries({ queryKey: crmKeys.all });
}

/**
 * Page frame of the CRM screens: app layout, CRM context, and the "no company" / "module off" states.
 * Children render only when the module is on.
 */
export function CrmShell({ currentPage, children }: { currentPage: string; children: (ctx: CrmContext) => ReactNode }) {
  const [, setLocation] = useLocation();
  const { tx } = useCrmText();
  const [user] = useState(readUser);
  const { data: ctx, isLoading, error } = useCrmContext();

  const logout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    setLocation("/");
  };

  let body: ReactNode;
  if (isLoading) {
    body = (
      <div className="flex items-center justify-center py-16 text-muted-foreground">
        <Loader2 className="mr-2 h-5 w-5 animate-spin" /> {tx("loading")}
      </div>
    );
  } else if (error || !ctx) {
    body = <Card><CardContent className="py-10 text-center text-sm text-destructive">{error?.message || tx("error")}</CardContent></Card>;
  } else if (!ctx.hasCompany) {
    body = <Card><CardContent className="py-10 text-center text-sm text-muted-foreground" data-testid="crm-no-company">{tx("noCompany")}</CardContent></Card>;
  } else if (!ctx.enabled) {
    body = (
      <Card data-testid="crm-disabled">
        <CardHeader>
          <CardTitle className="text-base">{tx("disabledTitle")}</CardTitle>
          <CardDescription>{tx("disabledDesc")}</CardDescription>
        </CardHeader>
        <CardContent>
          {ctx.isAdmin ? (
            <div className="flex flex-wrap items-center gap-3">
              <Button asChild data-testid="crm-open-settings">
                <Link href="/settings?section=crm"><Settings className="h-4 w-4" /> {tx("openSettings")}</Link>
              </Button>
              <span className="text-sm text-muted-foreground">{tx("enableInSettings")}</span>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">{tx("askAdmin")}</p>
          )}
        </CardContent>
      </Card>
    );
  } else {
    body = children(ctx);
  }

  return (
    <AppLayout user={user} onLogout={logout} currentPage={currentPage}>
      {body}
    </AppLayout>
  );
}
